import { useMemo, useRef, HTMLAttributes, useCallback } from 'react'
import { useSpring, config } from '@react-spring/web'
import { useDispatch } from 'react-redux'
import { ResumeFieldSuggest, SectionNames, selectors, actions } from 'builder/modules/resumeEditor'
import { useI18n } from 'builder/hooks/useI18n'
import { useTypedSelector } from 'builder/hooks/useTypedSelector'
import type { ToneName } from 'builder/styles/colors'
import FocusManager from 'builder/services/FocusManager'
import { getEmptyCard } from 'builder/modules/resumeEditor/resumeScoreRules/sectionRule'
import { generateRandomId } from 'builder/utils/generateRandomId'
import { constructSectionsData } from '../../constructSectionsData'
import { useStepByStepFlow } from '../../hooks/useStepByStepFlow'
import {
  Container,
  Header,
  HeaderMain,
  HeaderAside,
  Percent,
  Progress,
  ProgressInner,
  HeaderText,
  Suggestion,
  PercentSuggestion,
} from './styles'

export const ResumeScoreContainer = (props: HTMLAttributes<HTMLDivElement>) => {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const { i18n } = useI18n()
  const dispatch = useDispatch()
  const resume = useTypedSelector(selectors.resume)
  const resumeScore = resume?.resumeScore || 0
  const resumeOptimizerData = useTypedSelector(selectors.jobPostingAPIData)
  const { suggestions } = useTypedSelector(selectors.scoreSuggestions)
  const currentStep = useTypedSelector(selectors.stepByStepFlowCurrentStep)
  const { additionalSections } = resume ? constructSectionsData(resume) : { additionalSections: [] }
  const { handleChangeStep } = useStepByStepFlow()
  const suggestion = useMemo(() => {
    const requiredScore = 100 - resumeScore

    const item = suggestions?.find(item => {
      if (currentStep === SectionNames.addAdditionalSection) {
        return additionalSections.some(
          section => section.id === item.sectionId || section.id === item.sectionName,
        )
      }
      return item.sectionId === currentStep || item.sectionName === currentStep
    })

    if (!item) return { text: '', points: null }

    return {
      fieldId: item.fieldId,
      type: item.type,
      text: item.text,
      points: Math.min(item.points, requiredScore),
      key: item.key,
      sectionName: item.sectionName,
    }
  }, [additionalSections, currentStep, resumeScore, suggestions])

  const points = resumeOptimizerData.averageScore || resumeScore

  const [barColor] = useMemo(() => {
    const getColorByPoints = (points: number): ToneName => {
      if (points > 70) return 'green'
      if (points > 30) return 'amber'
      return 'red'
    }

    const color = getColorByPoints(points)
    return [`var(--${color}-40)`, `var(--${color}-50)`]
  }, [points])

  const springProps = useSpring({
    config: config.slow,
    from: { points: 0 },
    to: { points: Math.min(points, 100) },
  })

  const handleSuggestionClick = useCallback(
    (suggestion: ResumeFieldSuggest) => {
      if (suggestion.type === 'simpleField' && suggestion.fieldId) {
        FocusManager.focus(suggestion.fieldId)
      }
      const sectionName = suggestion.sectionName
      if (suggestion.type === 'section' && sectionName) {
        const emptyCard = resume && getEmptyCard(sectionName, resume)

        if (emptyCard) {
          const cardId = emptyCard.cid || emptyCard.id
          dispatch(actions.openCard({ id: cardId, sectionId: sectionName }))
          FocusManager.focus(cardId)
        } else {
          dispatch(
            actions.addCard({
              sectionName,
              cardId: generateRandomId(),
              options: { scrollIntoViewport: false, shouldOpen: true },
            }),
          )
        }
        handleChangeStep(sectionName)
      }
    },
    [dispatch, handleChangeStep, resume],
  )

  return (
    <Container ref={containerRef} {...props}>
      <Header>
        <HeaderMain>
          <Percent points={points}>{springProps.points.to(value => value.toFixed(0))}</Percent>
          <HeaderText>{i18n.t('builder.resume_tuner.banner.dashboard_score')}</HeaderText>
        </HeaderMain>

        {suggestion.points && (
          <HeaderAside>
            <Suggestion onClick={() => handleSuggestionClick(suggestion)}>
              <PercentSuggestion>+{suggestion.points}</PercentSuggestion>
              {suggestion.text}
            </Suggestion>
          </HeaderAside>
        )}
      </Header>
      <Progress>
        <ProgressInner
          style={{
            transform: springProps.points.to(value => `scaleX(${value / 100})`),
            backgroundColor: barColor,
          }}
        />
      </Progress>
    </Container>
  )
}
